import clsx from 'clsx';
import { type JSX, useState } from 'react';
import type { DecisionProbe } from 'typedefs/DecisionProbe';
import style from './RenderedCode.module.less';

type McDcCoverageBlockProps = {
	probe: DecisionProbe;
	lineNumberWidthInEm: number;
	probeCoverageColumnWidth: number;
	lineNumber: number;
};

/** A table row that renders a MC/DC coverage block. */
export function McDcCoverageBlockLine({
	probe,
	lineNumberWidthInEm,
	probeCoverageColumnWidth,
	lineNumber
}: McDcCoverageBlockProps): JSX.Element {
	const configurations = probe.configurations;
	const conditions = probe.conditions;

	const [hoveredConditionDescription, setHoveredConditionDescription] = useState('');
	let firstConfigurationIndex = -1;
	let secondConfigurationIndex = -1;
	if (hoveredConditionDescription.includes('+')) {
		const splitDescription = hoveredConditionDescription.split('+');
		firstConfigurationIndex = parseInt(splitDescription[0]!.trim()) - 1;
		secondConfigurationIndex = parseInt(splitDescription[1]!.trim()) - 1;
	} else if (hoveredConditionDescription.includes('-')) {
		const splitDescription = hoveredConditionDescription.split('-');
		firstConfigurationIndex = parseInt(splitDescription[0]!.trim()) - 1;
		secondConfigurationIndex = parseInt(splitDescription[1]!.trim()) - 1;
	}

	return (
		<>
			{configurations.map((configuration, index) => {
				return (
					<tr
						key={'mcdc-config-' + lineNumber + '-' + index}
						style={{ marginTop: index === 0 ? '0.5em' : undefined }}
						className="probe-configuration probe-annotation text-gray-500"
					>
						<td className={style.lineNumber} style={{ width: lineNumberWidthInEm + 'em' }} />
						<td
							style={{
								paddingLeft: '.5em',
								textAlign: 'right',
								width: probeCoverageColumnWidth + 'em'
							}}
						>
							{configuration.decisionValue ? configuration.executionCount : null}
						</td>
						<td
							style={{
								paddingRight: '.5em',
								textAlign: 'right',
								width: probeCoverageColumnWidth + 'em'
							}}
						>
							{!configuration.decisionValue ? configuration.executionCount : null}
						</td>
						<td colSpan={3} />
						<td
							className={clsx({
								'bg-yellow-200': firstConfigurationIndex === index || secondConfigurationIndex === index
							})}
						>
							<span
								data-position="right center"
								data-inverted=""
								data-tooltip="Valuation of conditionals: T = true, F = false, _ = 'don't care'"
							>
								{index + 1}: {configuration.description}
							</span>
						</td>
					</tr>
				);
			})}
			{conditions.map((condition, index) => {
				return (
					<tr
						key={'mcdc-condition-' + lineNumber + '-' + index}
						style={{ marginBottom: index === conditions.length - 1 ? '0.5em' : undefined }}
						className={clsx('probe-configuration probe-annotation', {
							'text-green-700': condition.fulfilled,
							'text-red-700': !condition.fulfilled
						})}
					>
						<td className={style.lineNumber} style={{ width: lineNumberWidthInEm + 'em' }} />
						<td
							className="pl-2 text-right"
							style={{
								width: probeCoverageColumnWidth + 'em'
							}}
						/>
						<td
							className="pr-2 text-right"
							style={{
								width: probeCoverageColumnWidth + 'em'
							}}
						/>
						<td colSpan={3} />
						<td
							onMouseEnter={() => setHoveredConditionDescription(condition.description)}
							onMouseLeave={() => setHoveredConditionDescription('')}
						>
							<span
								data-position="right center"
								data-inverted=""
								data-tooltip={
									`MC/DC criterion for condition ${String(index + 1)} ${
										condition.fulfilled ? 'fulfilled' : 'not fulfilled'
									} with valuations ` + condition.description
								}
							>
								MC/DC (cond {index + 1}): {condition.description}
							</span>
						</td>
					</tr>
				);
			})}
		</>
	);
}
